import { USED_PARTS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDateTimeInline, formatMoney, formatQuantity } from "../../../../utils";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { buildOrderedPartReferenceId } from "../../../../utils/entityRoutes";
import {
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
} from "../../../../components/ui/detailView";

export const UsedPartBasicInfo = () => {
  const { id, usedPartId } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(USED_PARTS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [usedPart, setUsedPart] = useState<any | null>(null);
  const canViewUsedParts = useHasPermission("service.view_usedpart");
  const canViewJobs = useHasPermission("service.view_job");
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");

  useEffect(() => {
    const fetchUsedPart = async () => {
      if (id && usedPartId) {
        const response = await get(usedPartId);
        if (response.data) {
          setUsedPart(response.data);
          const usedPartLabel = (t("Used for") + " " + response.data.job_info.number) || "";
          setOverride(`/service/stock-status/${id}/used-parts/${usedPartId}`, usedPartLabel);
        }
      }
    };

    const handleUsedPartUpdated = () => {
      fetchUsedPart();
    };

    window.addEventListener("usedPartUpdated", handleUsedPartUpdated);
    fetchUsedPart();
    return () => {
      window.removeEventListener("usedPartUpdated", handleUsedPartUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canViewUsedParts) {
    return null;
  }

  if (!usedPart) return <DetailPage loading />;

  return (
    <DetailPage>
      <DetailHeader
        icon={<HandymanOutlinedIcon />}
        title={`${t("Used for")} ${usedPart.job_info?.number || id}`}
        subtitle={usedPart.ordered_part_info?.number && `${t("From Order")} ${usedPart.ordered_part_info.number}`}
      />

      <DetailSection title={t("Basic Information")}>
        <DetailRow label={t("From Order")}>
          <EntityReferenceLink
            entityType="orderedPart"
            entityId={
              usedPart.ordered_part_info
                ? buildOrderedPartReferenceId(
                  usedPart.ordered_part_info.part_id,
                  usedPart.ordered_part_info.id
                )
                : undefined
            }
            label={usedPart.ordered_part_info?.number}
            canPreview={canViewOrderedParts}
          />
        </DetailRow>
        {usedPart.job_task && (
          <DetailRow label={t("Job")}>
            <EntityReferenceLink
              entityType="job"
              entityId={usedPart.job_info?.id}
              label={usedPart.job_info?.number}
              canPreview={canViewJobs}
            />
          </DetailRow>
        )}
        <DetailRow label={t("Quantity")} value={formatQuantity(usedPart.quantity ?? 0)} />
        <DetailRow label={t("Total Cost")} value={formatMoney(usedPart.total ?? 0)} />
        {/* The API has no dedicated "used at" field; the last change is the closest proxy. */}
        <DetailRow label={t("Used At")} value={formatDateTimeInline(usedPart.updated_at)} />
      </DetailSection>

      <DetailMetadata createdAt={usedPart.created_at} updatedAt={usedPart.updated_at} />
    </DetailPage>
  );
};
